/**
 * Service layer for Address Lookup.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AddressDetails, DEFAULT_ADDRESS_DETAILS } from '../../shared/models/address.types';

export interface AddressLookupOption {
  label: string;
  value: string;
  caption: string;
  structured?: AddressDetails;
}

/**
 * SERVICE: Address Lookup
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class AddressLookupService {
  private readonly http = inject(HttpClient);
  private readonly searchUrl = `${environment.apiBaseUrl}/aggregation/locations/search`;

  /**
   * BUSINESS LOGIC: search
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async search(query: string): Promise<AddressLookupOption[]> {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 3) {
      return [];
    }

    const response = await this.searchBackend(trimmedQuery, 5);

    return this.extractLocationRecords(response)
      .map((item) => this.mapOption(item))
      .filter((option): option is AddressLookupOption => option !== null);
  }

  /**
   * BUSINESS LOGIC: getStructuredAddress
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async getStructuredAddress(query: string): Promise<AddressDetails | null> {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return null;
    }

    const response = await this.searchBackend(trimmedQuery, 1);
    const first = this.extractLocationRecords(response)[0];

    if (!first) {
      return null;
    }

    return this.mapAddress(first);
  }

  /**
   * API NORMALIZATION: searchBackend
   * Inputs: Receives a user query plus the maximum number of location suggestions the caller needs.
   * Output: Returns the backend response exactly as received so response-shape handling stays centralized below.
   * Processing: Calls the authenticated aggregation gateway; the global auth interceptor owns bearer-token attachment.
   */
  private searchBackend(query: string, limit: number): Promise<unknown> {
    const params = new HttpParams()
      .set('q', query)
      .set('limit', String(limit));

    return firstValueFrom(this.http.get<unknown>(this.searchUrl, { params }));
  }

  /**
   * API NORMALIZATION: extractLocationRecords
   * Inputs: Receives either a bare array response or a wrapped aggregation response.
   * Output: Returns only object-like location records that can be mapped into address suggestions.
   * Processing: Supports common collection containers so minor backend envelope changes do not leak into callers.
   */
  private extractLocationRecords(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return this.toRecordArray(response);
    }

    const record = this.asRecord(response);
    const data = record['data'];

    if (Array.isArray(data)) {
      return this.toRecordArray(data);
    }

    const dataRecord = this.asRecord(data);
    const nestedItems = dataRecord['items'] ?? dataRecord['results'] ?? dataRecord['locations'];

    if (Array.isArray(nestedItems)) {
      return this.toRecordArray(nestedItems);
    }

    const rootItems = record['items'] ?? record['results'] ?? record['locations'];

    if (Array.isArray(rootItems)) {
      return this.toRecordArray(rootItems);
    }

    return [];
  }

  /**
   * API NORMALIZATION: mapOption
   * Inputs: Receives one backend location record.
   * Output: Returns a shared autocomplete option with a cached structured address, or null when no label exists.
   * Processing: Keeps the public `AddressLookupOption` contract stable for `ui-address-form`.
   */
  private mapOption(result: Record<string, unknown>): AddressLookupOption | null {
    const structured = this.mapAddress(result);
    const label = structured.formattedAddress.trim();

    if (!label) {
      return null;
    }

    return {
      label,
      value: label,
      caption: this.buildCaption(structured),
      structured
    };
  }

  /**
   * BUSINESS LOGIC: buildCaption
   * Inputs: Receives a normalized address record.
   * Output: Returns a compact caption for the autocomplete option.
   * Processing: Uses locality, region, and country so suggestions remain scannable without duplicating the full address.
   */
  private buildCaption(address: AddressDetails): string {
    return [address.locality, address.region, address.country].filter(Boolean).join(', ');
  }

  /**
   * API NORMALIZATION: mapAddress
   * Inputs: Receives one backend location record.
   * Output: Returns the shared `AddressDetails` shape used by every `ui-address-form` parent.
   * Processing: Reads both root-level and nested address fields plus common latitude/longitude shapes.
   */
  private mapAddress(result: Record<string, unknown>): AddressDetails {
    const details = this.asRecord(result['address']);
    const formattedAddress = this.readFirstString(result, details, [
      'formattedAddress',
      'displayName',
      'display_name',
      'description',
      'address',
      'name',
      'label'
    ]);

    return {
      ...DEFAULT_ADDRESS_DETAILS,
      formattedAddress,
      streetAddress: this.readFirstString(result, details, ['streetAddress', 'street', 'road', 'pedestrian']),
      locality: this.readFirstString(result, details, ['locality', 'city', 'town', 'village', 'municipality']),
      subLocality: this.readFirstString(result, details, ['subLocality', 'sub_locality', 'suburb', 'neighbourhood', 'quarter']),
      region: this.readFirstString(result, details, ['region', 'state', 'province']),
      country: this.readFirstString(result, details, ['country']),
      postalCode: this.readFirstString(result, details, ['postalCode', 'postal_code', 'postcode', 'zipCode', 'zip']),
      area: this.readFirstString(result, details, ['area', 'city_district', 'county', 'district', 'suburb', 'neighbourhood']),
      geo: this.readGeoCoordinates(result)
    };
  }

  /**
   * API NORMALIZATION: readGeoCoordinates
   * Inputs: Receives one backend location record.
   * Output: Returns coordinates in the shared `[longitude, latitude]` order.
   * Processing: Supports GeoJSON coordinates, flat coordinates arrays, and separate lat/lng style fields.
   */
  private readGeoCoordinates(result: Record<string, unknown>): [number, number] {
    const geo = this.asRecord(result['geo']);
    const geoCoordinates = this.readCoordinateArray(geo['coordinates']);

    if (geoCoordinates) {
      return geoCoordinates;
    }

    const coordinates = this.readCoordinateArray(result['coordinates']);

    if (coordinates) {
      return coordinates;
    }

    const longitude = this.readNumber(result['lng'] ?? result['lon'] ?? result['longitude']);
    const latitude = this.readNumber(result['lat'] ?? result['latitude']);

    return [longitude ?? 0, latitude ?? 0];
  }

  /**
   * API NORMALIZATION: readCoordinateArray
   * Inputs: Receives a potential backend coordinate array.
   * Output: Returns a finite `[longitude, latitude]` tuple or null.
   * Processing: Drops malformed arrays so callers can continue with the zero-coordinate fallback.
   */
  private readCoordinateArray(value: unknown): [number, number] | null {
    if (!Array.isArray(value)) {
      return null;
    }

    const longitude = this.readNumber(value[0]);
    const latitude = this.readNumber(value[1]);

    if (longitude === null && latitude === null) {
      return null;
    }

    return [longitude ?? 0, latitude ?? 0];
  }

  /**
   * API NORMALIZATION: readFirstString
   * Inputs: Receives root and nested backend records plus candidate field names.
   * Output: Returns the first non-empty string value from either record.
   * Processing: Keeps backend field-name drift out of shared form components.
   */
  private readFirstString(root: Record<string, unknown>, nested: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const rootValue = this.readString(root[key]);

      if (rootValue) {
        return rootValue;
      }

      const nestedValue = this.readString(nested[key]);

      if (nestedValue) {
        return nestedValue;
      }
    }

    return '';
  }

  /**
   * API NORMALIZATION: readString
   * Inputs: Receives any candidate backend field value.
   * Output: Returns a trimmed string when the value is usable.
   * Processing: Avoids rendering object-like address payloads as `[object Object]` labels.
   */
  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * API NORMALIZATION: readNumber
   * Inputs: Receives any candidate coordinate field value.
   * Output: Returns a finite number or null.
   * Processing: Accepts numeric strings from backend geocoders without preserving invalid values.
   */
  private readNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * API NORMALIZATION: toRecordArray
   * Inputs: Receives a raw backend collection.
   * Output: Returns only object records from that collection.
   * Processing: Filters primitive or null entries so downstream mapping can stay object-safe.
   */
  private toRecordArray(values: unknown[]): Record<string, unknown>[] {
    return values
      .map((value) => this.asRecord(value))
      .filter((record) => Object.keys(record).length > 0);
  }

  /**
   * API NORMALIZATION: asRecord
   * Inputs: Receives any backend value.
   * Output: Returns the object record shape or an empty object fallback.
   * Processing: Centralizes unknown-response guarding for the address lookup mapper.
   */
  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  }
}
